import {
  EDP_WS_ID,
  PHILOSOPHIES,
  PAGE_REGIONS,
  WORKSPACE_TEMPLATES,
  VIEW_MODES,
  OBJECT_DETAIL_TABS,
  TOOLBAR_ACTIONS,
  DEMO_TIMELINE,
  buildShellDashboard,
} from '../../workspaceshell/framework';
import { WorkspaceShell } from '../../components/workspace';
import { Button, Card, KpiCard } from '../../components/ui';

export function WorkspaceArchitecturePage() {
  const dashboard = buildShellDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{EDP_WS_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Universal Workspace Architecture</h1>
        <p className="mt-1 text-sm text-slate-500">
          Workspace templates, layout standards, panel architecture, and shell components — the blueprint every module inherits.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Workspace Types" value={dashboard.workspace_types.toString()} />
        <KpiCard label="Page Regions" value={dashboard.page_regions.toString()} />
        <KpiCard label="View Modes" value={dashboard.view_modes.toString()} />
        <KpiCard label="Toolbar Actions" value={dashboard.toolbar_actions.toString()} />
        <KpiCard label="Object Detail Tabs" value={dashboard.object_detail_tabs.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Workspace Philosophy</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {PHILOSOPHIES.map((p) => (
            <Card key={p.id} padding="sm">
              <p className="font-medium">{p.label}</p>
              <p className="mt-1 text-xs text-slate-500">{p.description}</p>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Workspace Templates</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {WORKSPACE_TEMPLATES.map((t) => (
            <Card key={t.type} padding="sm">
              <p className="font-medium">{t.label}</p>
              {t.description && <p className="mt-1 text-xs text-slate-500">{t.description}</p>}
              {t.primary_pattern && (
                <p className="mt-2 text-[10px] text-slate-400">Pattern: {t.primary_pattern}</p>
              )}
              {t.modules && (
                <p className="mt-1 text-[10px] text-slate-400">{t.modules.join(', ')}</p>
              )}
            </Card>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Page Regions</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {PAGE_REGIONS.map((r) => (
              <span key={r.id} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">
                {r.label}
              </span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">View Modes</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {VIEW_MODES.map((v) => (
              <span key={v.id} className="rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-300">
                {v.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Object Detail Tabs</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {OBJECT_DETAIL_TABS.map((tab) => (
            <span key={tab.id} className="rounded-md bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800">
              {tab.label}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Toolbar Actions</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {TOOLBAR_ACTIONS.map((action) => (
            <Button key={action} variant="secondary" size="sm">{action}</Button>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Live Shell Preview</h2>
        <p className="mt-1 text-sm text-slate-500">Analytics workspace template with explorer, insights, AI panel, and timeline.</p>
        <div className="mt-4 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
          <WorkspaceShell
            title="Revenue Command Center"
            subtitle="Analytics workspace · demo shell"
            template="analytics"
            timeline={DEMO_TIMELINE}
          />
        </div>
      </section>

      <Card className="border-brand-200 bg-brand-50/30 dark:border-brand-900 dark:bg-brand-950/20">
        <p className="text-sm font-medium">Developer Implementation</p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Registry: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">pkg/workspaceshell/</code> ·
          Shell: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">components/workspace/WorkspaceShell.tsx</code> ·
          Tenant entity: <code className="rounded bg-white/60 px-1 dark:bg-slate-800">pkg/workspace/</code> (distinct)
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/edp/EDP-00004_ENTERPRISE_WORKSPACE_ARCHITECTURE.md</code>
        </p>
      </Card>
    </div>
  );
}
